import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import { ContractLedger } from '@wsphere/warranties/domain';
import { IconButtonColumn, MultiBadgeColumn, TableColumn, TextColumn } from '@vsolv/vectors/table';
import { from, map, Observable } from 'rxjs';
import { DeletePartyDialog, PARTY_ROLE_OPTIONS, PartyDialog } from '../../dialogs';
import { PartyListItem, RatingMatrixService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.utils';

// Past this, the rest of the list is in the cell's tooltip rather than growing the row.
const USAGE_LINES_SHOWN = 5;

/**
 * Tenant-wide fee-recipient parties — the insurers, agents, administrator and others a Fee Type
 * default or rating-matrix cell override can name. Like dimensions, these are shared across
 * policies; the tab sits beside Dimensions for the same reason.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-parties-page',
  templateUrl: './policy-parties.page.html',
  standalone: false,
})
export class PolicyPartiesPage implements OnInit {
  @ViewChild('partyDialog') partyDialog!: PartyDialog;
  @ViewChild('deletePartyDialog') deletePartyDialog!: DeletePartyDialog;

  readonly roleOptions = PARTY_ROLE_OPTIONS;

  parties: PartyListItem[] = [];
  loading = false;
  search = '';
  roleFilter: ContractLedger.PartyType | 'all' = 'all';

  canEdit$ = from(this.securitySvc.hasAccess('pol_Edit', null));

  // Same table and icon-button columns as the Dimensions tab.
  columns$: Observable<TableColumn<unknown>[]> = this.canEdit$.pipe(
    map(canEdit => [
      // TextColumn text and tooltips render through innerHTML — escape everything tenant-entered.
      new TextColumn<PartyListItem>({ header: 'Name' }, party => ({
        text: escapeHtml(party.name),
        classes: 'font-semibold',
      })),
      new MultiBadgeColumn<PartyListItem>({ header: 'Roles' }, party => ({
        wrap: true,
        items: party.roles.map(role => ({ text: this.roleLabel(role), theme: 'default' as const })),
      })),
      new TextColumn<PartyListItem>({ header: 'Assigned to' }, party => ({
        text: this.describeUsage(party),
        // One assignment per line.
        classes: 'text-gray-600 whitespace-pre-line',
        tooltip: party.usage.length > USAGE_LINES_SHOWN ? party.usage.map(escapeHtml).join('<br>') : undefined,
      })),
      ...(canEdit
        ? [
            new IconButtonColumn<PartyListItem>({ stickyEnd: true, fitContent: true }, party => ({
              type: 'clear' as const,
              rounded: true,
              icon: 'edit-01' as const,
              click: () => this.edit(party),
            })),
            new IconButtonColumn<PartyListItem>({ stickyEnd: true, fitContent: true }, party => ({
              type: 'clear' as const,
              rounded: true,
              icon: 'trash-01' as const,
              click: () => this.remove(party),
            })),
          ]
        : []),
    ])
  );

  constructor(
    private ratingMatrixSvc: RatingMatrixService,
    private securitySvc: SecurityService,
    private toastSvc: ToastService
  ) {}

  async ngOnInit() {
    await this.load();
  }

  async load() {
    this.loading = true;
    try {
      this.parties = await this.ratingMatrixSvc.listParties({
        ...(this.roleFilter !== 'all' && { role: this.roleFilter }),
        ...(this.search.trim() && { search: this.search.trim() }),
      });
    } catch (err) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to load parties',
        text: escapeHtml((err as { error?: { message?: string } })?.error?.message ?? 'Something went wrong.'),
      });
    } finally {
      this.loading = false;
    }
  }

  async onRoleFilterChange(value: unknown) {
    this.roleFilter = typeof value === 'string' ? (value as ContractLedger.PartyType | 'all') : 'all';
    await this.load();
  }

  roleLabel(role: ContractLedger.PartyType): string {
    return ContractLedger.partyTypeLabel(role);
  }

  describeUsage(party: PartyListItem): string {
    if (!party.usage.length) return 'Not assigned';
    const shown = party.usage.slice(0, USAGE_LINES_SHOWN);
    const hidden = party.usage.length - shown.length;
    // TextColumn renders through innerHTML, so escape the titles rather than let them be read as markup.
    return [...shown.map(escapeHtml), ...(hidden ? [`…and ${hidden} more`] : [])].join('\n');
  }

  create() {
    this.partyDialog.open();
  }

  edit(party: PartyListItem) {
    this.partyDialog.open(party);
  }

  remove(party: PartyListItem) {
    this.deletePartyDialog.open(party);
  }

  async onDialogClosed(saved: boolean) {
    if (saved) await this.load();
  }
}
